import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { logAndThrow } from "../utils/logging";
import { LOGIN_GROUP_PREFIX, useNotificationContext } from "../contexts/NotificationContext";
import { makeNotifier } from "../utils/notify";
import { GlassButton } from "./ui/GlassComponents";

const Auth: React.FC = () => {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [view, setView] = useState<"login" | "signup" | "recovery">("login");
  const { showNotification } = useNotificationContext();
  const notify = makeNotifier((message, type) =>
    showNotification(message, type, {
      group: `${LOGIN_GROUP_PREFIX}auth`,
      autoCloseMs: type === "error" ? 8000 : 4000,
    })
  );

  const handleAuth = async () => {
    setLoading(true);
    if (view === "signup") {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: fullName } },
        });
        if (error) return logAndThrow("auth.signUp", error, { email });
        if (data.user?.identities?.length === 0) {
          notify.error(t("signup.error_user_exists"));
        } else {
          notify.success(t("signup.success_check_email"));
          setView("login");
        }
      } catch (e: any) {
        notify.error(e.message || String(e));
      }
    } else {
      // 'login' view
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error)
          return logAndThrow("auth.signInWithPassword", error, { email });
        notify.success(t("login.success"));
      } catch (e: any) {
        notify.error(e.message || String(e));
      }
    }
    setLoading(false);
  };

  const handlePasswordRecovery = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error)
        return logAndThrow("auth.resetPasswordForEmail", error, { email });
      notify.success(t("recovery.success_check_inbox"));
      setView("login");
    } catch (e: any) {
      notify.error(e.message || String(e));
    }
    setLoading(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (view === "recovery") {
      handlePasswordRecovery();
    } else {
      handleAuth();
    }
  };

  const subtitles = {
    login: t("login.page_subtitle"),
    signup: t("signup.page_subtitle"),
    recovery: t("recovery.page_subtitle"),
  };

  const buttonLabels = {
    login: { default: t("login.submit"), loading: t("login.loading") },
    signup: { default: t("signup.submit"), loading: t("signup.loading") },
    recovery: {
      default: t("reset_password.submit"),
      loading: t("reset_password.loading"),
    },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white/5 p-4">
      <div className="w-full max-w-md bg-white/[0.02] backdrop-blur-lg rounded-lg shadow-overlay border border-white/10 p-4 space-y-3">
        <div className="flex flex-col items-center space-y-2">
          <img src="/tool.png" alt="App Logo" className="h-14 w-14" />
          <h1 className="text-3xl font-bold text-white">Moubarati - مباراتي</h1>
          <p className="text-white/60">{subtitles[view]}</p>
        </div>

        <form className="space-y-2" onSubmit={handleSubmit}>
          {view === "signup" && (
            <div>
              <label
                htmlFor="full-name"
                className="block text-sm font-medium text-white/80"
              >
                {t("labels.full_name")}
              </label>
              <input
                id="full-name"
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                type="text"
                placeholder={t("placeholders.full_name")}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                autoComplete="name"
              />
            </div>
          )}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-white/80"
            >
              {t("labels.email")}
            </label>
            <input
              id="email"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              type="email"
              placeholder={t("placeholders.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          {view !== "recovery" && (
            <div>
              <div className="flex justify-between items-center">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-white/80"
                >
                  {t("labels.password")}
                </label>
              </div>
              <input
                id="password"
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-md shadow-sm py-2 px-3 text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                type="password"
                placeholder={t("placeholders.password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={
                  view === "login" ? "current-password" : "new-password"
                }
              />
            </div>
          )}

          <GlassButton
            type="submit"
            variant="primary"
            size="md"
            loading={loading}
            className="w-full"
          >
            {loading ? buttonLabels[view].loading : buttonLabels[view].default}
          </GlassButton>
        </form>

        <p className="text-sm text-center text-white/60">
          {view === "recovery" && (
            <>
              {t("recovery.back_prefix")}{" "}
              <button
                onClick={() => setView("login")}
                className="font-medium text-primary-500 hover:underline ms-1"
              >
                {t("recovery.back_link")}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default Auth;
